import React from 'react';
import { Briefcase, Calendar, MapPin, Building2, Terminal } from 'lucide-react';
import { portfolioData } from '../data/portfolioData';

export const Experience: React.FC = () => {
  return (
    <section id="experience" className="section experience-section">
      <div className="container">
        <div className="section-header">
          <div className="section-badge">
            <Briefcase size={14} />
            <span>Career Journey</span>
          </div>
          <h2 className="section-title">
            Work <span className="text-gradient">Experience</span>
          </h2>
          <p className="section-subtitle">
            12+ years leading engineering teams, engineering enterprise platforms, and shipping high-impact products.
          </p>
        </div>

        <div className="timeline-container">
          {portfolioData.experiences.map((exp, index) => (
            <div key={index} className="timeline-item">
              <div className="timeline-marker">
                <div className="marker-dot"></div>
                {index !== portfolioData.experiences.length - 1 && (
                  <div className="timeline-line"></div>
                )}
              </div>

              <div className="timeline-content glass-card">
                <div className="exp-header">
                  <div>
                    <h3 className="exp-title">{exp.title}</h3>
                    <div className="exp-company">
                      <Building2 size={16} />
                      <span>{exp.company}</span>
                    </div>
                  </div>

                  <div className="exp-meta">
                    <span className="exp-date">
                      <Calendar size={14} />
                      {exp.startDate} - {exp.endDate}
                    </span>
                    <span className="exp-location">
                      <MapPin size={14} />
                      {exp.address}
                    </span>
                  </div>
                </div>

                <p className="exp-desc">{exp.description}</p>

                {/* Key technology badges */}
                <div className="exp-tech-section">
                  <div className="tech-heading">
                    <Terminal size={14} />
                    <span>Technologies & Frameworks:</span>
                  </div>
                  <div className="tech-tags">
                    {exp.technologies.map((tech) => (
                      <span key={tech} className="tech-tag">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        .experience-section {
          background: var(--bg-secondary);
        }

        .timeline-container {
          max-width: 900px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 2.5rem;
          position: relative;
        }

        .timeline-item {
          display: grid;
          grid-template-columns: 40px 1fr;
          gap: 1.5rem;
          position: relative;
        }

        .timeline-marker {
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
        }

        .marker-dot {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: var(--gradient-brand);
          border: 4px solid var(--bg-secondary);
          box-shadow: var(--shadow-glow);
          z-index: 2;
        }

        .timeline-line {
          position: absolute;
          top: 20px;
          bottom: -40px;
          width: 2px;
          background: var(--border-color);
          z-index: 1;
        }

        .timeline-content {
          padding: 2rem;
        }

        .exp-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1rem;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .exp-title {
          font-size: 1.35rem;
          font-weight: 700;
          color: var(--text-main);
          margin-bottom: 0.35rem;
        }

        .exp-company {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          font-size: 0.95rem;
          color: var(--accent-primary);
          font-weight: 600;
        }

        .exp-meta {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 0.35rem;
        }

        .exp-date, .exp-location {
          display: flex;
          align-items: center;
          gap: 0.35rem;
          font-size: 0.85rem;
          color: var(--text-muted);
        }

        .exp-desc {
          font-size: 1rem;
          color: var(--text-muted);
          line-height: 1.7;
          margin-bottom: 1.5rem;
          white-space: pre-line;
        }

        .exp-tech-section {
          padding-top: 1rem;
          border-top: 1px solid var(--border-color);
        }

        .tech-heading {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          font-size: 0.85rem;
          color: var(--text-dim);
          font-weight: 600;
          margin-bottom: 0.75rem;
        }

        .tech-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .tech-tag {
          padding: 0.3rem 0.75rem;
          border-radius: var(--radius-sm);
          background: rgba(99, 102, 241, 0.1);
          border: 1px solid rgba(99, 102, 241, 0.2);
          color: var(--text-main);
          font-size: 0.82rem;
          font-weight: 500;
        }

        @media (max-width: 768px) {
          .timeline-item {
            grid-template-columns: 24px 1fr;
            gap: 1rem;
          }
          .exp-header {
            flex-direction: column;
          }
          .exp-meta {
            align-items: flex-start;
          }
        }
      `}</style>
    </section>
  );
};
